import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Loader2,
  Save,
  User,
  GraduationCap,
  Users as FamilyIcon,
  Sparkles,
  MapPin,
  Phone,
  Heart,
  ShieldCheck,
  ImagePlus,
  X,
  Camera,
} from "lucide-react";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import Combobox from "../../Components/Combobox";
import { compressImage } from "../../utils/imageCompressor";
import {
  indiaAddressOptions,
  indiaStates,
  residenceTypes,
} from "../../data/addressOptions";
import countriesList from "../../data/countries";

/* =====================================================================
   Options (same as CreateProfile)
===================================================================== */
const heightOptions = Array.from({ length: 25 }, (_, idx) => {
  const totalInches = 54 + idx;
  return `${Math.floor(totalInches / 12)}'${totalInches % 12}"`;
});

const SELECT = {
  gender: ["Male", "Female"],
  maritalStatus: ["Never Married", "Divorced", "Widowed", "Awaiting Divorce", "Marriage Settled"],
  height: heightOptions,
  religion: ["Hindu", "Converted Christian"],
  caste: ["Goud / Gowda", "Settibalija", "Ediga", "Srisayana", "Yatha"],
  rashi: ["Mesha (మేషం)","Vrishabha (వృషభం)","Mithuna (మిథునం)","Karka (కర్కాటకం)","Simha (సింహం)","Kanya (కన్య)","Tula (తుల)","Vrischika (వృశ్చికం)","Dhanu (ధనుస్సు)","Makara (మకరం)","Kumbha (కుంభం)","Meena (మీనం)"],
  nakshatram: ["Ashwini (అశ్విని)","Bharani (భరణి)","Krittika (కృత్తిక)","Rohini (రోహిణి)","Mrigashira (మృగశిర)","Ardra (ఆర్ద్ర)","Punarvasu (పునర్వసు)","Pushya (పుష్యమి)","Ashlesha (ఆశ్లేష)","Magha (మఘ)","Purva Phalguni (పూర్వ ఫల్గుణి)","Uttara Phalguni (ఉత్తర ఫల్గుణి)","Hasta (హస్త)","Chitra (చిత్ర)","Swati (స్వాతి)","Vishakha (విశాఖ)","Anuradha (అనురాధ)","Jyeshtha (జ్యేష్ఠ)","Moola (మూల)","Purva Ashadha (పూర్వాషాఢ)","Uttara Ashadha (ఉత్తరాషాఢ)","Shravana (శ్రవణం)","Dhanishta (ధనిష్ఠ)","Shatabhisha (శతభిషం)","Purva Bhadrapada (పూర్వాభాద్ర)","Uttara Bhadrapada (ఉత్తరాభాద్ర)","Revati (రేవతి)"],
  collegeType: ["IIT","NIT","IIIT","IIM","AIIMS","BITS","Government University","Government College","Private University","Private College","Deemed University","Foreign University","Other"],
  education: ["10th","Intermediate / 12th","Diploma","ITI","B.A","B.Com","B.Sc","B.Tech / B.E","BBA","BCA","LLB","CA","CS","MBBS","BDS","B.Pharmacy","M.A","M.Com","M.Sc","MS (Master of Science)","M.Tech / M.E","MBA","MCA","MDS","MD / MS","Ph.D","Other"],
  profession: ["Software Engineer","Doctor","Nurse","Pharmacist","Engineer","Teacher / Lecturer","Professor","Government Employee","Banking Professional","Accountant","Chartered Accountant","Business Owner","Self Employed","Farmer / Agriculture","Lawyer","Police","Defence Personnel","Civil Services","Sales / Marketing","Designer","Architect","Consultant","Entrepreneur","Student","Homemaker","Not Working","Other"],
  visaStatus: ["Citizen","Permanent Resident","Green Card","Work Visa","Student Visa","Dependent Visa","Visitor Visa","Temporary Resident","Other"],
  annualIncome: ["Not Working","Below 2 Lakhs","2-5 Lakhs","5-10 Lakhs","10-15 Lakhs","15-25 Lakhs","25-50 Lakhs","50 Lakhs - 1 Crore","Above 1 Crore","Prefer not to say"],
  familyFinancialStatus: ["Lower Middle Class","Middle Class","Upper Middle Class","Rich","Prefer Not to Say"],
  membership: ["free", "premium"],
  status: ["active", "blocked", "suspended"],
  profileStatus: ["Draft", "Pending Verification", "Verified", "Rejected", "Changes Requested"],
};

const interestOptions = [
  "Music","Travel","Cooking","Reading","Movies","Fitness","Devotional",
  "Photography","Dancing","Gardening","Sports","Art","Yoga","Foodie","Tech","Outdoors","Pets",
];

/* =====================================================================
   Field component
===================================================================== */
const Field = ({ label, children }) => (
  <div>
    <label className="mb-1.5 block text-[12px] font-semibold uppercase tracking-wider text-ink-400">
      {label}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, type = "text", placeholder, multiline, disabled }) => (
  multiline ? (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      disabled={disabled}
      className="w-full resize-none rounded-2xl border border-sand-300 bg-white px-4 py-3 text-[14px] text-ink-800 outline-none transition focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10 disabled:bg-sand-100 disabled:text-ink-400"
    />
  ) : (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full rounded-2xl border border-sand-300 bg-white px-4 py-3 text-[14px] text-ink-800 outline-none transition focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10 disabled:bg-sand-100 disabled:text-ink-400"
    />
  )
);

/* =====================================================================
   Main
===================================================================== */
const AdminEditProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [mobileEditable, setMobileEditable] = useState(false);
  const [showMobileConfirm, setShowMobileConfirm] = useState(false);
  const [dragging, setDragging] = useState(false);
  const savedFormRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "profiles", userId));
        if (!snap.exists()) {
          setError("Profile not found.");
          setLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() };
        setForm(data);
        savedFormRef.current = data;
      } catch (err) {
        setError(err.message || "Could not load profile.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  const hasUnsavedChanges = () => {
    if (!savedFormRef.current) return false;
    return JSON.stringify(form) !== JSON.stringify(savedFormRef.current) || imageFiles.length > 0;
  };

  const handleBackNavigation = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true);
    } else {
      navigate(`/admin/users/${userId}`);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
    setNotice("");
  };

  const toggleInterest = (i) => {
    const current = form.interests || [];
    const selected = current.includes(i);
    updateField("interests", selected ? current.filter((x) => x !== i) : [...current, i]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { id, createdAt, ...data } = form;
      await updateDoc(doc(db, "profiles", userId), {
        ...data,
        updatedAt: serverTimestamp(),
      });
      savedFormRef.current = { ...form };
      setShowSuccess(true);
    } catch (err) {
      setError(err.message || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-brand-600" size={32} />
        </div>
      </AdminLayout>
    );
  }

  if (error && !form.id) {
    return (
      <AdminLayout>
        <div className="card p-8 text-center text-rose-600">{error}</div>
      </AdminLayout>
    );
  }

  const isIndia = (form.residenceType || "India") === "India";
  const indiaDistricts = Object.keys(indiaAddressOptions[form.state] || {});
  const indiaCities = indiaAddressOptions[form.state]?.[form.district] || [];

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleBackNavigation}
            className="inline-flex items-center gap-1.5 rounded-2xl border border-sand-200 bg-white px-3 py-2 text-[12.5px] font-semibold text-ink-700 shadow-soft transition hover:border-brand-600 hover:text-brand-600"
          >
            <ArrowLeft size={14} /> Back
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-ink-800 sm:text-2xl">
              Edit: {form.fullName || "Profile"}
            </h1>
            <p className="text-[12px] text-ink-400">
              {form.kmProfileId || userId} · Admin edit
            </p>
          </div>
      </div>

      {notice && (
        <div className="mb-4 rounded-2xl bg-emerald-50 px-4 py-3 text-[13px] font-semibold text-emerald-700">
          {notice}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-2xl bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-700">
          {error}
        </div>
      )}

      {/* Form sections */}
      <div className="space-y-5">
        {/* Account / Admin fields */}
        <Section title="Account & Status" Icon={ShieldCheck}>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Membership">
              <Combobox value={form.membership} onChange={(v) => updateField("membership", v)} options={SELECT.membership} placeholder="Select" />
            </Field>
            <Field label="Account Status">
              <Combobox value={form.status} onChange={(v) => updateField("status", v)} options={SELECT.status} placeholder="Select" />
            </Field>
            <Field label="Profile Status">
              <Combobox value={form.profileStatus} onChange={(v) => updateField("profileStatus", v)} options={SELECT.profileStatus} placeholder="Select" />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Verification Remarks">
              <Input value={form.verificationRemarks} onChange={(v) => updateField("verificationRemarks", v)} multiline placeholder="Admin notes for user..." />
            </Field>
          </div>
        </Section>

        {/* Personal */}
        <Section title="Personal Information" Icon={User}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Full Name"><Input value={form.fullName} onChange={(v) => updateField("fullName", v)} /></Field>
            <Field label="Gender"><Combobox value={form.gender} onChange={(v) => updateField("gender", v)} options={SELECT.gender} placeholder="Select" /></Field>
            <Field label="Marital Status"><Combobox value={form.maritalStatus} onChange={(v) => updateField("maritalStatus", v)} options={SELECT.maritalStatus} placeholder="Select" /></Field>
            <Field label="Date of Birth"><Input type="date" value={form.dateOfBirth} onChange={(v) => updateField("dateOfBirth", v)} /></Field>
            <Field label="Height"><Combobox value={form.height} onChange={(v) => updateField("height", v)} options={SELECT.height} placeholder="Select" /></Field>
            <Field label="Religion"><Combobox value={form.religion} onChange={(v) => updateField("religion", v)} options={SELECT.religion} placeholder="Select" /></Field>
            <Field label="Caste"><Combobox value={form.caste} onChange={(v) => updateField("caste", v)} options={SELECT.caste} placeholder="Select" /></Field>
            <Field label="Gotram"><Input value={form.gotram} onChange={(v) => updateField("gotram", v)} /></Field>
            <Field label="Rashi"><Combobox value={form.rashi} onChange={(v) => updateField("rashi", v)} options={SELECT.rashi} placeholder="Select" /></Field>
            <Field label="Nakshatram"><Combobox value={form.nakshatram} onChange={(v) => updateField("nakshatram", v)} options={SELECT.nakshatram} placeholder="Select" searchPlaceholder="Search nakshatram..." /></Field>
            <Field label="Birth Place"><Input value={form.birthPlace} onChange={(v) => updateField("birthPlace", v)} /></Field>
          </div>
          <div className="mt-3">
            <Field label="About Me"><Input value={form.aboutMe} onChange={(v) => updateField("aboutMe", v)} multiline /></Field>
          </div>
        </Section>

        {/* Education & Career */}
        <Section title="Education & Career" Icon={GraduationCap}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Education"><Combobox value={form.education} onChange={(v) => updateField("education", v)} options={SELECT.education} placeholder="Select" /></Field>
            {form.education && form.education !== "10th" && form.education !== "Intermediate / 12th" && (
              <>
                <Field label="College Type"><Combobox value={form.collegeType} onChange={(v) => updateField("collegeType", v)} options={SELECT.collegeType} placeholder="Select" searchPlaceholder="Search type..." /></Field>
                <Field label="College"><Input value={form.collegeName} onChange={(v) => updateField("collegeName", v)} /></Field>
              </>
            )}
            <Field label="Profession"><Combobox value={form.profession} onChange={(v) => updateField("profession", v)} options={SELECT.profession} placeholder="Select" /></Field>
            <Field label="Company"><Input value={form.companyName} onChange={(v) => updateField("companyName", v)} /></Field>
            <Field label="Working Place"><Input value={form.workingPlace} onChange={(v) => updateField("workingPlace", v)} /></Field>
            <Field label="Annual Income"><Combobox value={form.annualIncome} onChange={(v) => updateField("annualIncome", v)} options={SELECT.annualIncome} placeholder="Select" /></Field>
            <div className="sm:col-span-2 lg:col-span-3">
              <Field label="Other Information"><Input value={form.otherInformation} onChange={(v) => updateField("otherInformation", v)} multiline placeholder="Optional" /></Field>
            </div>
          </div>
        </Section>

        {/* Family */}
        <Section title="Family" Icon={FamilyIcon}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Father Name"><Input value={form.fatherName} onChange={(v) => updateField("fatherName", v)} /></Field>
            <Field label="Father Profession"><Input value={form.fatherProfession} onChange={(v) => updateField("fatherProfession", v)} /></Field>
            <Field label="Mother Name"><Input value={form.motherName} onChange={(v) => updateField("motherName", v)} /></Field>
            <Field label="Mother Profession"><Input value={form.motherProfession} onChange={(v) => updateField("motherProfession", v)} /></Field>
            <Field label="Brothers"><Input type="number" value={form.brothers} onChange={(v) => updateField("brothers", v)} /></Field>
            <Field label="Married Brothers"><Input type="number" value={form.marriedBrothers} onChange={(v) => updateField("marriedBrothers", v)} /></Field>
            <Field label="Sisters"><Input type="number" value={form.sisters} onChange={(v) => updateField("sisters", v)} /></Field>
            <Field label="Married Sisters"><Input type="number" value={form.marriedSisters} onChange={(v) => updateField("marriedSisters", v)} /></Field>
            <Field label="Financial Status"><Combobox value={form.familyFinancialStatus} onChange={(v) => updateField("familyFinancialStatus", v)} options={SELECT.familyFinancialStatus} placeholder="Select" /></Field>
          </div>
        </Section>

        {/* Address */}
        <Section title="Address" Icon={MapPin}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Residence Type"><Combobox value={form.residenceType} onChange={(v) => updateField("residenceType", v)} options={residenceTypes} placeholder="Select" /></Field>
            {isIndia ? (
              <>
                <Field label="State"><Combobox value={form.state} onChange={(v) => updateField("state", v)} options={indiaStates} placeholder="Select" /></Field>
                <Field label="District"><Combobox value={form.district} onChange={(v) => updateField("district", v)} options={indiaDistricts} placeholder="Select" disabled={!form.state} /></Field>
                <Field label="City / Town"><Combobox value={form.cityTown} onChange={(v) => updateField("cityTown", v)} options={indiaCities} placeholder="Select" disabled={!form.district} /></Field>
                <Field label="Area"><Input value={form.area} onChange={(v) => updateField("area", v)} /></Field>
                <Field label="Pincode"><Input value={form.pincode} onChange={(v) => updateField("pincode", v)} /></Field>
              </>
            ) : (
              <>
                <Field label="Country"><Combobox value={form.addressCountry} onChange={(v) => updateField("addressCountry", v)} options={countriesList} placeholder="Select" /></Field>
                <Field label="State / Province"><Input value={form.state} onChange={(v) => updateField("state", v)} placeholder="Enter state / province" /></Field>
                <Field label="City"><Input value={form.cityTown} onChange={(v) => updateField("cityTown", v)} placeholder="Enter city" /></Field>
                <Field label="Visa Status"><Combobox value={form.visaStatus} onChange={(v) => updateField("visaStatus", v)} options={SELECT.visaStatus} placeholder="Select" /></Field>
              </>
            )}
            <Field label="Present Residence"><Input value={form.presentResidence} onChange={(v) => updateField("presentResidence", v)} /></Field>
          </div>
        </Section>

        {/* Contact */}
        <Section title="Contact" Icon={Phone}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Mobile Number">
              <div className="flex items-center gap-2">
                <Input
                  value={form.mobileNumber}
                  onChange={(v) => updateField("mobileNumber", v)}
                  disabled={!mobileEditable}
                />
                {!mobileEditable ? (
                  <button
                    type="button"
                    onClick={() => setShowMobileConfirm(true)}
                    className="shrink-0 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-[11.5px] font-semibold text-amber-700 transition hover:bg-amber-100"
                  >
                    Edit
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setMobileEditable(false)}
                    className="shrink-0 rounded-xl border border-sand-200 bg-white px-3 py-2.5 text-[11.5px] font-semibold text-ink-500"
                  >
                    Lock
                  </button>
                )}
              </div>
              {!mobileEditable && (
                <p className="mt-1 text-[10.5px] text-amber-600">⚠️ Locked. Click Edit to change (requires confirmation).</p>
              )}
            </Field>
            <Field label="Alternate Number"><Input value={form.alternateNumber} onChange={(v) => updateField("alternateNumber", v)} /></Field>
          </div>
        </Section>

        {/* Mobile number confirm modal */}
        {showMobileConfirm && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/50 px-4 backdrop-blur-sm" onClick={() => setShowMobileConfirm(false)}>
            <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-elevated" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-3 px-6 pt-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                  <Phone size={20} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-ink-800">Edit mobile number?</h3>
                  <p className="mt-1 text-[13px] leading-6 text-ink-500">
                    Changing the primary mobile number will affect the user's login. Are you sure you want to proceed?
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 px-6 py-5">
                <button onClick={() => setShowMobileConfirm(false)} className="btn-ghost py-3">Cancel</button>
                <button
                  onClick={() => { setMobileEditable(true); setShowMobileConfirm(false); }}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-amber-500 px-5 py-3 text-[14px] font-semibold text-white transition hover:bg-amber-600"
                >
                  Yes, enable edit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Interests */}
        <Section title="Interests" Icon={Heart}>
          <div className="flex flex-wrap gap-2">
            {interestOptions.map((it) => {
              const selected = (form.interests || []).includes(it);
              return (
                <button
                  key={it}
                  type="button"
                  onClick={() => toggleInterest(it)}
                  className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition ${
                    selected
                      ? "border-brand-600 bg-brand-gradient text-white"
                      : "border-sand-300 bg-white text-ink-700 hover:border-brand-600"
                  }`}
                >
                  {selected && <Check size={10} className="mr-1 inline" />}
                  {it}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Photos */}
        <Section title="Photos" Icon={Camera}>
          {/* Drag and drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const files = Array.from(e.dataTransfer.files).filter(
                (f) => f.type.startsWith("image/") && f.size <= 5 * 1024 * 1024
              );
              if (files.length) {
                setImageFiles((prev) => [...prev, ...files.map((f) => ({
                  file: f,
                  preview: URL.createObjectURL(f),
                  id: `${f.name}-${Date.now()}-${Math.random()}`,
                }))]);
              }
            }}
            className={`relative flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-5 text-center transition ${
              dragging
                ? "border-brand-600 bg-brand-50"
                : "border-sand-300 bg-white hover:border-brand-600"
            }`}
          >
            <ImagePlus size={22} className={dragging ? "text-brand-600" : "text-ink-400"} />
            <span className="mt-2 text-[13px] font-semibold text-ink-800">
              {dragging ? "Drop photos here" : "Drag & drop or click to add photos"}
            </span>
            <span className="text-[11px] text-ink-400">JPG / PNG / WEBP, max 5 MB each</span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="absolute inset-0 cursor-pointer opacity-0"
              onChange={(e) => {
                const files = Array.from(e.target.files || []).filter(
                  (f) => f.type.startsWith("image/") && f.size <= 5 * 1024 * 1024
                );
                setImageFiles((prev) => [...prev, ...files.map((f) => ({
                  file: f,
                  preview: URL.createObjectURL(f),
                  id: `${f.name}-${Date.now()}-${Math.random()}`,
                }))]);
              }}
            />
          </div>

          {/* New files to upload */}
          {imageFiles.length > 0 && (
            <div className="mt-3">
              <p className="text-[11.5px] font-semibold text-ink-400 mb-2">New photos (not yet saved):</p>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                {imageFiles.map((item) => (
                  <div key={item.id} className="relative">
                    <img src={item.preview} alt="New" className="aspect-square w-full rounded-xl object-cover object-center ring-2 ring-amber-200" />
                    <button
                      type="button"
                      onClick={() => {
                        URL.revokeObjectURL(item.preview);
                        setImageFiles((prev) => prev.filter((f) => f.id !== item.id));
                      }}
                      className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-rose-600 shadow"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Upload progress */}
              {uploading && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11.5px] font-semibold">
                    <span className="text-brand-600">Uploading...</span>
                    <span className="text-ink-700">{uploadProgress}%</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-sand-200">
                    <div
                      className="h-full rounded-full bg-brand-gradient transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <button
                type="button"
                disabled={uploading}
                onClick={async () => {
                  setUploading(true);
                  setUploadProgress(0);
                  try {
                    const urls = [];
                    const totalFiles = imageFiles.length;
                    for (let i = 0; i < totalFiles; i++) {
                      const item = imageFiles[i];
                      const compressed = await compressImage(item.file).catch(() => item.file);
                      const safeName = item.file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
                      const imageRef = ref(storage, `profile-images/${userId}/${Date.now()}-${safeName}`);

                      // Upload with progress
                      const url = await new Promise((resolve, reject) => {
                        const uploadTask = uploadBytesResumable(imageRef, compressed);
                        uploadTask.on("state_changed",
                          (snapshot) => {
                            const fileProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                            const overallProgress = Math.round(((i + fileProgress / 100) / totalFiles) * 100);
                            setUploadProgress(overallProgress);
                          },
                          (err) => reject(err),
                          async () => {
                            const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
                            resolve(downloadUrl);
                          }
                        );
                      });
                      urls.push(url);
                    }

                    setUploadProgress(100);
                    const newImageUrls = [...(form.imageUrls || []), ...urls];
                    updateField("imageUrls", newImageUrls);
                    await updateDoc(doc(db, "profiles", userId), { imageUrls: newImageUrls, updatedAt: serverTimestamp() });
                    imageFiles.forEach((f) => URL.revokeObjectURL(f.preview));
                    setImageFiles([]);
                    setNotice(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded successfully.`);
                  } catch (err) {
                    setError(err.message || "Upload failed.");
                  } finally {
                    setUploading(false);
                    setUploadProgress(0);
                  }
                }}
                className="btn-primary mt-3 px-4 py-2.5 text-[12.5px]"
              >
                {uploading ? <><Loader2 className="animate-spin" size={13} /> Uploading...</> : <><ImagePlus size={13} /> Upload {imageFiles.length} photo{imageFiles.length > 1 ? "s" : ""}</>}
              </button>
            </div>
          )}

          {/* Existing photos */}
          {(form.imageUrls || []).length > 0 ? (
            <div className="mt-4">
              <p className="text-[11.5px] font-semibold text-ink-400 mb-2">
                Current photos ({form.imageUrls.length}) · First photo is the default profile picture
              </p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {form.imageUrls.map((url, idx) => (
                  <div key={url} className={`relative rounded-xl ${idx === 0 ? "ring-2 ring-brand-600 ring-offset-1" : "ring-1 ring-sand-200"}`}>
                    <img src={url} alt={`Photo ${idx + 1}`} className="aspect-square w-full rounded-xl object-cover object-center" />
                    {/* Set as default */}
                    {idx !== 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          const reordered = [url, ...form.imageUrls.filter((u) => u !== url)];
                          updateField("imageUrls", reordered);
                        }}
                        title="Set as default"
                        className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white/90 text-ink-500 shadow hover:text-brand-600"
                      >
                        <Camera size={9} />
                      </button>
                    )}
                    {/* Delete */}
                    <button
                      type="button"
                      onClick={async () => {
                        const updated = form.imageUrls.filter((u) => u !== url);
                        updateField("imageUrls", updated);
                        try {
                          const storageRef = ref(storage, url);
                          await deleteObject(storageRef);
                        } catch {
                          // ignore — URL might not be a direct storage ref
                        }
                      }}
                      className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-rose-600 shadow hover:bg-rose-50"
                    >
                      <X size={10} />
                    </button>
                    {idx === 0 && (
                      <span className="absolute inset-x-0 bottom-0 rounded-b-xl bg-brand-600/90 py-0.5 text-center text-[8px] font-bold uppercase text-white">
                        Default
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="mt-3 text-[13px] text-ink-400">No photos uploaded yet.</p>
          )}
        </Section>

        {/* Spacer for sticky bar */}
        <div className="h-20" />
      </div>

      {/* Sticky save bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-sand-200 bg-white/95 px-5 py-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/admin/users/${userId}`)}
            className="btn-ghost px-5 py-2.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn-primary px-6 py-2.5"
          >
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
            Save changes
          </button>
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      {showUnsavedModal && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-ink-900/50 px-4 backdrop-blur-sm sm:items-center" onClick={() => setShowUnsavedModal(false)}>
          <div className="w-full max-w-md overflow-hidden rounded-t-[28px] bg-white shadow-elevated sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col items-center px-6 pt-8 pb-2 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
                <AlertTriangle size={24} />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold text-ink-800">Unsaved changes</h3>
              <p className="mt-2 text-[13.5px] leading-6 text-ink-500">
                You have unsaved changes. What would you like to do?
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 px-6 py-5">
              <button
                type="button"
                onClick={() => {
                  setShowUnsavedModal(false);
                  navigate(`/admin/users/${userId}`);
                }}
                className="btn-ghost py-3"
              >
                Discard
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUnsavedModal(false);
                  handleSave();
                }}
                className="btn-primary py-3"
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/50 px-4 backdrop-blur-sm">
          <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-elevated">
            <div className="flex flex-col items-center px-6 pt-8 pb-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                <Check size={28} className="text-emerald-600" />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold text-ink-800">
                Changes saved!
              </h3>
              <p className="mt-2 text-[13.5px] leading-6 text-ink-500">
                Profile has been updated successfully.
              </p>
            </div>
            <div className="border-t border-sand-200 px-6 py-4">
              <button
                type="button"
                onClick={() => navigate(`/admin/users/${userId}`)}
                className="btn-primary w-full py-3"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const Section = ({ title, Icon, children }) => (
  <section className="card p-5 sm:p-6">
    <div className="mb-4 flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
        <Icon size={18} />
      </div>
      <h2 className="font-display text-lg font-bold text-ink-800">{title}</h2>
    </div>
    {children}
  </section>
);

export default AdminEditProfile;
